/// <amd-module name="VSS/ExtServices/HostNavigationService" />
import { ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { getCurrentRoute, getDisplayedNavigation, getNavigation } from "VSS/Platform/Navigation";
import { toLookup } from "VSS/Platform/Util/Array";
import { Uri } from "VSS/Platform/Util/Url";

export const HostNavigationServiceName = "ms.vss-features.host-navigation-service";

/**
 * Route information for the current page
 */
export interface IPageRoute {
    /**
     * Id of the contributed route
     */
    id: string;

    /**
     * The route values that were specified in the request
     */
    routeValues: { [key: string]: string };
}

/**
 * Information about a displayed navigation element
 */
export interface INavigationElement {
    /**
     * Unique id of the displayed navigation
     */
    id: string;

    /**
     * Name of the displayed navigation
     */
    name?: string;

    /**
     * Type of the navigation element
     */
    type: string;
}

/**
 * Service for external content to interact with the host frame's navigation functionalities
 */
export interface IHostNavigationService {
    /**
     * Gets the current hash.
     */
    getHash(): string;

    /**
     * Gets the set of navigation elements (like hubs and hub groups) selected on the current page.
     */
    getPageNavigationElements(): INavigationElement[];

    /**
     * Gets information about the route that was matched for the current page
     */
    getPageRoute(): IPageRoute;

    /**
     * Gets the current set of query parameters in the host page's URL.
     */
    getQueryParams(): { [key: string]: string };

    /**
     * Navigate the parent page to the specified url
     *
     * @param url Url to navigate to
     */
    navigate(url: string): void;

    /**
     * Add a callback to be invoked each time the hash navigation has changed
     *
     * @param callback Method invoked on each navigation hash change
     */
    onHashChanged(callback: (hash: string) => void): void;

    /**
     * Open a new window to the specified url
     *
     * @param url Url of the new window
     * @param features Comma-separated list of features/specs sent as the 3rd parameter to window.open. For example: "height=400,width=400".
     */
    openNewWindow(url: string, features: string): void;

    /**
     * Reloads the parent frame
     */
    reload(): void;

    /**
     * Replace existing hash with the provided hash from the hosted content.
     */
    replaceHash(hash: string): void;

    /**
     * Update the host document's title (appears as the browser tab title).
     *
     * @param title The new title of the window
     */
    setDocumentTitle(title: string): void;

    /**
     * Sets the provided hash from the hosted content.
     */
    setHash(hash: string): void;

    /**
     * Sets one or more query parameters on the host page
     *
     * @param parameters Dictionary of query string parameters to add, update, or remove (pass an empty value to remove)
     */
    setQueryParams(parameters: { [key: string]: string }): void;
}

class HostNavigationService extends VssService implements IHostNavigationService {
    public getHash(): string {
        return window.location.hash;
    }

    public getPageNavigationElements(): INavigationElement[] {
        const navElements: INavigationElement[] = [];
        const navElementIds = getDisplayedNavigation(this.pageContext);
        for (const navElementId of navElementIds) {
            const navElement = getNavigation(this.pageContext, navElementId);
            if (navElement) {
                navElements.push({
                    id: navElement.id,
                    name: navElement.name,
                    type: navElement.type
                });
            }
        }
        return navElements;
    }

    public getPageRoute(): IPageRoute {
        const currentRoute = getCurrentRoute(this.pageContext);
        return {
            id: currentRoute.routeId,
            routeValues: currentRoute.routeValues
        };
    }

    public getQueryParams(): { [key: string]: string } {
        const uri = new Uri(window.location.href);
        return toLookup(uri.queryParameters.filter(q => !!q.value), q => q.name, q => q.value!);
    }

    public navigate(url: string): void {
        window.location.href = url;
    }

    public onHashChanged(callback: (hash: string) => void): void {
        if (callback) {
            window.addEventListener(
                "hashchange",
                () => {
                    callback(window.location.hash);
                },
                false
            );
        }
    }

    public openNewWindow(url: string, features: string): void {
        const win = window.open(url, "_blank", features);
        if (win) {
            win.opener = null;
        }
    }

    public reload(): void {
        window.location.reload();
    }

    public replaceHash(hash: string): void {
        window.location.replace("#" + hash);
    }

    public setDocumentTitle(title: string): void {
        document.title = title;
    }

    public setHash(hash: string): void {
        window.location.hash = hash;
    }

    public setQueryParams(parameters: { [key: string]: string }): void {
        const uri = new Uri(window.location.href);
        uri.addQueryParams(parameters, true);
        for (const key of Object.keys(parameters)) {
            const value = parameters[key];
            if (value) {
                uri.addQueryParam(key, value, true);
            } else {
                uri.removeQueryParam(key);
            }
        }

        window.history.replaceState(history.state, document.title, uri.absoluteUri);
    }
}

Services.add(HostNavigationServiceName, { serviceFactory: HostNavigationService, options: ServiceOptions.Remotable });
